import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { getTeamOptions } from "../../../../services/team";
import { getMonitoringHistory, exportMonitoringPDF, MonitoringSeriesImages } from "../../../../services/planning";
import { buildSeriesBlocks, formatDuration, SeriesBlock } from "../../../../services/monitoring-series";
import { getTeamSeasonAthletes } from "../../../../services/athlete";
import { getSeasonCurrent } from "../../../../services/season";
import { Team } from "../../../../interfaces/team";
import moment from "moment";
import * as htmlToImage from 'html-to-image';
import { MonitoringLaptimesChart } from "../../../charts/monitoring-laptimes-chart";
import { MonitoringShovelfulsChart } from "../../../charts/monitoring-shovelfuls-chart";

export function CoachMonitoringHistory() {
    const [loaded, setLoaded] = useState(false);
    const [teamOptions, setTeamOptions] = useState<Team[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState(0);
    const [athleteOptions, setAthleteOptions] = useState<any[]>([]);
    const [selectedAthleteId, setSelectedAthleteId] = useState(0);
    const [currentSeasonId, setCurrentSeasonId] = useState(0);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [monitoringList, setMonitoringList] = useState<any[]>([]);
    const [searched, setSearched] = useState(false);
    const [expandedSessions, setExpandedSessions] = useState<number[]>([]);
    const [exportingPDF, setExportingPDF] = useState<number | null>(null);
    const sessionRefs = useRef<Record<number, HTMLDivElement | null>>({});
    // Refs per session per series index (e.g. seriesRefs[monitoring_id][seriesIndex])
    const summaryBoxesRefs = useRef<Record<number, Record<number, HTMLDivElement | null>>>({});
    const laptimesChartRefs = useRef<Record<number, Record<number, HTMLDivElement | null>>>({});
    const shovelfulsChartRefs = useRef<Record<number, Record<number, HTMLDivElement | null>>>({});

    const ensureSessionRefs = (monitoringId: number) => {
        if (!summaryBoxesRefs.current[monitoringId]) summaryBoxesRefs.current[monitoringId] = {};
        if (!laptimesChartRefs.current[monitoringId]) laptimesChartRefs.current[monitoringId] = {};
        if (!shovelfulsChartRefs.current[monitoringId]) shovelfulsChartRefs.current[monitoringId] = {};
    };

    const sessionSeriesBlocks = (session: any): SeriesBlock[] => {
        return buildSeriesBlocks(session.lap_events || [], session.shovelfuls_data || []);
    };

    useEffect(() => {
        Promise.all([
            getTeamOptions(),
            getSeasonCurrent()
        ]).then(([teamsResponse, seasonResponse]) => {
            setTeamOptions(teamsResponse.data || []);
            if (seasonResponse.data && seasonResponse.data.id) {
                setCurrentSeasonId(seasonResponse.data.id);
                if (seasonResponse.data.date_start) {
                    setDateFrom(seasonResponse.data.date_start);
                }
                if (seasonResponse.data.date_end) {
                    setDateTo(seasonResponse.data.date_end);
                }

                // Cargar datos automáticamente con las fechas de la temporada
                if (seasonResponse.data.date_start && seasonResponse.data.date_end) {
                    setSearched(true);
                    getMonitoringHistory(0, 0, seasonResponse.data.date_start, seasonResponse.data.date_end)
                        .then(response => {
                            setMonitoringList(response.data || []);
                        })
                        .catch(error => {
                            console.error('Error loading monitoring history:', error);
                            setMonitoringList([]);
                        });
                }
            }
            setLoaded(true);
        }).catch(error => {
            console.error('Error loading options:', error);
            setLoaded(true);
        });
    }, []);

    const handleTeamChange = (teamId: number) => {
        setSelectedTeamId(teamId);
        setSelectedAthleteId(0);
        setAthleteOptions([]);

        if (teamId > 0 && currentSeasonId > 0) {
            getTeamSeasonAthletes(teamId, currentSeasonId)
                .then(response => {
                    setAthleteOptions(response.data || []);
                })
                .catch(error => {
                    console.error('Error loading athletes:', error);
                });
        }
    };

    const handleSearch = () => {
        setSearched(true);
        setExpandedSessions([]);

        getMonitoringHistory(selectedTeamId, selectedAthleteId, dateFrom, dateTo)
            .then(response => {
                setMonitoringList(response.data || []);
            })
            .catch(error => {
                console.error('Error loading monitoring history:', error);
                setMonitoringList([]);
            });
    };

    const toggleSession = (monitoringId: number) => {
        setExpandedSessions(prev =>
            prev.includes(monitoringId)
                ? prev.filter(id => id !== monitoringId)
                : [...prev, monitoringId]
        );
    };

    // Calcular agregados
    const getAggregates = () => {
        if (monitoringList.length === 0) return null;

        let totalDurationSeconds = 0;
        monitoringList.forEach((session: any) => {
            if (session.duration_seconds) {
                totalDurationSeconds += session.duration_seconds;
            }
        });

        const totalHours = Math.floor(totalDurationSeconds / 3600);
        const totalMinutes = Math.floor((totalDurationSeconds % 3600) / 60);

        return {
            totalSessions: monitoringList.length,
            totalDurationFormatted: totalHours > 0 ? `${totalHours}h ${totalMinutes}m` : `${totalMinutes}m`,
        };
    };

    const formatSessionDuration = (durationSeconds: number) => {
        const hours = Math.floor(durationSeconds / 3600);
        const minutes = Math.floor((durationSeconds % 3600) / 60);
        const seconds = Math.floor(durationSeconds % 60);
        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`;
        }
        return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    };

    const handleExportPDF = async (session: any) => {
        try {
            setExportingPDF(session.monitoring_id);

            // Wait for charts to render
            await new Promise(resolve => setTimeout(resolve, 300));

            const captureOptions = {
                quality: 1.0,
                pixelRatio: 2,
                backgroundColor: '#ffffff',
                cacheBust: true,
                skipFonts: true,
                preferredFontFormat: 'woff2' as const,
                filter: (node: HTMLElement) => {
                    if (node.tagName === 'STYLE') return false;
                    if (node.tagName === 'LINK' && node.getAttribute('rel') === 'stylesheet') return false;
                    return true;
                }
            };

            const captureNode = (node: HTMLElement | null): Promise<string | null> => {
                if (!node) return Promise.resolve(null);
                return htmlToImage.toPng(node, captureOptions);
            };

            const blocks = sessionSeriesBlocks(session);
            const seriesBlocks = blocks.filter(b => b.type === 'series') as Extract<SeriesBlock, { type: 'series' }>[];

            const seriesImages: MonitoringSeriesImages[] = await Promise.all(
                seriesBlocks.map(async (block) => {
                    const idx = block.index;
                    const [summaryImg, shovelfulsImg, laptimesImg] = await Promise.all([
                        captureNode(summaryBoxesRefs.current[session.monitoring_id]?.[idx] || null),
                        captureNode(shovelfulsChartRefs.current[session.monitoring_id]?.[idx] || null),
                        captureNode(laptimesChartRefs.current[session.monitoring_id]?.[idx] || null),
                    ]);
                    return {
                        summary: summaryImg,
                        chart_shovelfuls: shovelfulsImg,
                        chart_laptimes: laptimesImg,
                    };
                })
            );

            const response = await exportMonitoringPDF(
                session.monitoring_id,
                seriesImages.length > 0
                    ? { series: seriesImages }
                    : { summary: null, chart_shovelfuls: null, chart_laptimes: null }
            );

            if (response.data.success) {
                const binaryString = atob(response.data.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = response.data.filename || `monitoring-${session.date}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                alert(t("Error generating PDF. Try again later."));
            }
        } catch (error) {
            console.error('Error exporting monitoring PDF:', error);
            alert(t("Error generating PDF. Try again later."));
        } finally {
            setExportingPDF(null);
        }
    };

    if (!loaded) return <LoadingSpinner />;

    const aggregates = getAggregates();

    return (
        <div className="monitoring-history-container">
            <h1>{t("Monitoring history")}</h1>

            {/* Filtros */}
            <div className="monitoring-history-filters mb-4">
                <Row className="g-3 align-items-end">
                    <Col xs={12} md={3}>
                        <Form.Group>
                            <Form.Label>{t("Team")}</Form.Label>
                            <Form.Select
                                value={selectedTeamId}
                                onChange={(e) => handleTeamChange(parseInt(e.target.value))}
                            >
                                <option value={0}>{t("All teams")}</option>
                                {teamOptions.map((team: Team) => (
                                    <option key={team.id} value={team.id}>{team.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Group>
                            <Form.Label>{t("Athlete")}</Form.Label>
                            <Form.Select
                                value={selectedAthleteId}
                                onChange={(e) => setSelectedAthleteId(parseInt(e.target.value))}
                                disabled={selectedTeamId === 0}
                            >
                                <option value={0}>{t("All athletes")}</option>
                                {athleteOptions.map((athlete: any) => (
                                    <option key={athlete.athlete_id} value={athlete.athlete_id}>{athlete.athlete_name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Group>
                            <Form.Label>{t("From")} *</Form.Label>
                            <Form.Control
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Group>
                            <Form.Label>{t("To")} *</Form.Label>
                            <Form.Control
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={2}>
                        <Button variant="primary" onClick={handleSearch} className="w-100" disabled={!dateFrom || !dateTo}>
                            <i className="bi bi-search"></i> {t("Search")}
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Agregados */}
            {aggregates && (
                <div className="monitoring-history-aggregates mb-4">
                    <Row className="g-3">
                        <Col xs={6} md>
                            <div className="aggregate-box">
                                <div className="aggregate-value">{aggregates.totalSessions}</div>
                                <div className="aggregate-label">{t("Sessions")}</div>
                            </div>
                        </Col>
                        <Col xs={6} md>
                            <div className="aggregate-box">
                                <div className="aggregate-value">{aggregates.totalDurationFormatted}</div>
                                <div className="aggregate-label">{t("Total time")}</div>
                            </div>
                        </Col>
                    </Row>
                </div>
            )}

            {/* Timeline */}
            {searched && monitoringList.length === 0 && (
                <p className="text-muted text-center">{t("No monitoring sessions found")}</p>
            )}

            {monitoringList.length > 0 && (
                <div className="monitoring-history-timeline">
                    {monitoringList.map((session: any) => (
                        <div key={session.monitoring_id} className="timeline-item">
                            <div className="timeline-marker"></div>
                            <div className="timeline-content">
                                <div
                                    className="timeline-header"
                                    onClick={() => toggleSession(session.monitoring_id)}
                                >
                                    <div className="timeline-date">
                                        <i className={`bi ${expandedSessions.includes(session.monitoring_id) ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>{' '}
                                        <strong>{moment(session.date).format('D MMMM YYYY')}</strong>
                                        {session.day_moment && <span className="text-muted"> - {t(session.day_moment)}</span>}
                                    </div>
                                    <div className="timeline-meta">
                                        {session.team_name && <span className="badge bg-primary me-2">{session.team_name}</span>}
                                        {session.duration_seconds > 0 && (
                                            <span className="me-2"><i className="bi bi-hourglass-split"></i> {formatSessionDuration(session.duration_seconds)}</span>
                                        )}
                                        {session.lap_count > 0 && (
                                            <span className="me-2"><i className="bi bi-flag"></i> {session.lap_count} {t("laps")}</span>
                                        )}
                                        {session.avg_shovelfuls > 0 && (
                                            <span><i className="bi bi-activity"></i> {parseFloat(session.avg_shovelfuls).toFixed(1)} {t("strokes/min")}</span>
                                        )}
                                    </div>
                                </div>

                                {expandedSessions.includes(session.monitoring_id) && (() => {
                                    ensureSessionRefs(session.monitoring_id);
                                    const blocks = sessionSeriesBlocks(session);
                                    const workSeriesCount = blocks.filter(b => b.type === 'series').length;
                                    return (
                                    <div className="timeline-details" ref={(el) => { sessionRefs.current[session.monitoring_id] = el; }}>
                                        {/* Atletas */}
                                        {session.athletes && session.athletes.length > 0 && (
                                            <div className="mb-3">
                                                <strong>{t("Athletes")}:</strong>
                                                <div className="mt-1">
                                                    {session.athletes.map((a: any) => (
                                                        <a
                                                            key={a.athlete_id}
                                                            href={`#/athletes/${a.athlete_id}/view`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="athlete-badge"
                                                        >
                                                            {a.athlete_name}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Series */}
                                        {blocks.length === 0 && (
                                            <div className="summary-boxes mb-3" ref={(el) => { summaryBoxesRefs.current[session.monitoring_id][1] = el; }}>
                                                <div className="summary-box">
                                                    <div className="label">{ t("Start") }</div>
                                                    <div className="value">{session.start_time ? moment(session.start_time).format('HH:mm:ss') : '-'}</div>
                                                </div>
                                                <div className="summary-box">
                                                    <div className="label">{ t("Finish") }</div>
                                                    <div className="value">{session.finish_time ? moment(session.finish_time).format('HH:mm:ss') : '-'}</div>
                                                </div>
                                                <div className="summary-box">
                                                    <div className="label">{ t("Duration") }</div>
                                                    <div className="value">{session.duration_seconds > 0 ? formatSessionDuration(session.duration_seconds) : '-'}</div>
                                                </div>
                                                <div className="summary-box">
                                                    <div className="label">{ t("Laps") }</div>
                                                    <div className="value">{session.lap_count || 0}</div>
                                                </div>
                                                <div className="summary-box">
                                                    <div className="label">{ t("Average strokes/min") }</div>
                                                    <div className="value">{session.avg_shovelfuls > 0 ? parseFloat(session.avg_shovelfuls).toFixed(1) : '-'}</div>
                                                </div>
                                            </div>
                                        )}

                                        {blocks.map((block, bi) => {
                                            if (block.type === 'rest') {
                                                return (
                                                    <div key={`rest-${bi}`} className="series-rest-band mb-3">
                                                        <strong>{t("Recovery")}:</strong> {formatDuration(block.duration, true)}
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div key={`series-${block.index}`} className="series-block mb-4">
                                                    {workSeriesCount > 1 && (
                                                        <h5 className="series-heading">{t("Series")} {block.index}</h5>
                                                    )}
                                                    <div className="summary-boxes mb-3" ref={(el) => { summaryBoxesRefs.current[session.monitoring_id][block.index] = el; }}>
                                                        <div className="summary-box">
                                                            <div className="label">{t("Duration")}</div>
                                                            <div className="value">{formatDuration(block.totalDurationSeconds, true)}</div>
                                                        </div>
                                                        <div className="summary-box">
                                                            <div className="label">{t("Laps")}</div>
                                                            <div className="value">{block.lapDurations.length}</div>
                                                        </div>
                                                        <div className="summary-box">
                                                            <div className="label">{t("Average strokes/min")}</div>
                                                            <div className="value">{block.avgShovelfuls !== null ? block.avgShovelfuls.toFixed(1) : '-'}</div>
                                                        </div>
                                                    </div>
                                                    {block.shovelfuls.length > 0 && (
                                                        <div className="mb-3" ref={(el) => { shovelfulsChartRefs.current[session.monitoring_id][block.index] = el; }}>
                                                            <MonitoringShovelfulsChart
                                                                data={block.shovelfuls}
                                                                timestamps={block.shovelfulsTimestamps}
                                                            />
                                                        </div>
                                                    )}
                                                    {block.lapDurations.length > 0 && (
                                                        <div className="mb-3" ref={(el) => { laptimesChartRefs.current[session.monitoring_id][block.index] = el; }}>
                                                            <MonitoringLaptimesChart data={block.lapDurations} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* Comentarios */}
                                        {session.observations && (
                                            <div className="mb-2">
                                                <strong>{t("Coach comments")}:</strong>
                                                <p className="mt-1">{session.observations}</p>
                                            </div>
                                        )}

                                        {/* Acciones */}
                                        <div className="mt-3 d-flex gap-2">
                                            <a
                                                href={`#/coach/training/monitoring/${session.team_id}/${session.date}/${session.day_moment}/${session.day_moment_session}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-outline-primary btn-sm"
                                            >
                                                <i className="bi bi-box-arrow-up-right"></i> {t("Open monitoring session")}
                                            </a>
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="pdf-export-btn"
                                                onClick={() => handleExportPDF(session)}
                                                disabled={exportingPDF === session.monitoring_id}
                                            >
                                                {exportingPDF === session.monitoring_id ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                        {t("Generating...")}
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-file-earmark-pdf"></i> {t("Export PDF")}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                    );
                                })()}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
